import { ElementDto, SceneTypeDto } from "@repo/api/overlays";
import { Sequence, useVideoConfig } from "remotion";
import TextElement from "./TextElement.tsx";
import ImageElement from "./ImageElement.tsx";
import { AssetMediaAsyncDto } from "@repo/api/asset-media";
import VideoElement from "./VideoElement.tsx";
import RectangleElement from "./RectangleElement.tsx";
import { getHexWithOpacity, hexToRgba } from "../../utils/color.ts";
import { ElementTiming, msToFrames } from "../../utils/timing.ts";
import { useOpacity } from "../../utils/animation.ts";
import { CSSProperties, useEffect, useRef } from "react";

export type MovingElement = {
  elementType: ElementDto["type"];

  x: number;
  y: number;
  initialX: number;
  initialY: number;

  w: number;
  h: number;
  initialW: number;
  initialH: number;

  fontSize: number;
  initialFontSize: number;
};

export type ElementProps = {
  element: ElementDto;
  elementTiming: ElementTiming | null;
  assetMedias: Record<string, AssetMediaAsyncDto>;
  sceneType: SceneTypeDto;
  movingElement?: MovingElement;
  updateBoundingBox?: (divElement: HTMLDivElement | null) => void;
  isEditor?: boolean;
};

export function Element({
  element,
  elementTiming,
  assetMedias,
  movingElement,
  updateBoundingBox,
  isEditor,
}: ElementProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { fps } = useVideoConfig();

  useEffect(() => {
    if (updateBoundingBox) {
      updateBoundingBox(ref.current);
    }
  });

  const updateBoundingBoxRef = useRef(updateBoundingBox);
  updateBoundingBoxRef.current = updateBoundingBox; // eslint-disable-line

  // Make box around text element follow in size when text element is resized (e.g. when font is changed)
  useEffect(() => {
    const divElement = ref.current;
    if (!divElement || !updateBoundingBox) {
      return;
    }
    if (element.type !== "TEXT") {
      return;
    }
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateBoundingBoxRef.current?.(divElement);
    });
    observer.observe(divElement);

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.type]);

  useEffect(() => {
    return () => {
      if (updateBoundingBox) {
        updateBoundingBox(null);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const x = movingElement?.x ?? element.position.x;
  const y = movingElement?.y ?? element.position.y;

  let width: number | undefined;
  let height: number | undefined;

  if ("size" in element) {
    width = movingElement?.w ?? element.size.w;
    height = movingElement?.h ?? element.size.h;
  }

  let outlineStyle: CSSProperties["outlineStyle"] | undefined;
  let outlineWidth: number | undefined;
  let outlineColor: string | undefined;

  if ("border" in element && element.border !== null) {
    outlineStyle = "solid";
    outlineWidth = element.border.width;
    outlineColor = getHexWithOpacity(
      element.border.color.color,
      element.border.color.opacity ?? 100,
    );
  }

  let shadow: string | undefined;

  if ("shadow" in element && element.shadow !== null) {
    shadow = `drop-shadow(${element.shadow.x}px ${element.shadow.y}px ${element.shadow.blur}px ${hexToRgba(element.shadow.color.color, (element.shadow.color.opacity ?? element.shadow.opacity) / 100)})`;
  }

  const opacity = useOpacity({ element, elementTiming });

  if (!elementTiming) {
    return null;
  }

  // Skip hidden elements in the final render / playback. In the editor we keep
  // the node mounted (opacity 0) so its bounding box stays selectable.
  if (element.isHidden && !isEditor) {
    return null;
  }

  const effectiveOpacity = element.isHidden && isEditor ? 0 : opacity;

  return (
    <Sequence
      from={elementTiming.sceneStartAtFrame}
      durationInFrames={elementTiming.durationInFrames}
      style={{ pointerEvents: "none" }}
      premountFor={msToFrames(2000, fps)}
    >
      <div
        ref={ref}
        className="absolute cursor-default select-none touch-none"
        style={{
          top: 0,
          left: 0,
          width: width,
          height: height,
          transform: `translate3d(${x}px, ${y}px, 0)`,
          willChange: movingElement ? "transform, width, height" : undefined,
        }}
      >
        <div
          className="h-full w-full overflow-hidden"
          style={{
            outlineStyle: outlineStyle,
            outlineWidth: outlineWidth,
            outlineColor: outlineColor,
            filter: shadow,
            borderRadius:
              "cornerRadius" in element ? element.cornerRadius : undefined,
            opacity: effectiveOpacity / 100,
            overflow: "hidden",
          }}
        >
          {element.type === "TEXT" && (
            <TextElement element={element} fontSize={movingElement?.fontSize} />
          )}
          {element.type === "IMAGE" && (
            <ImageElement element={element} assetMedias={assetMedias} />
          )}
          {element.type === "VIDEO" && (
            <VideoElement element={element} assetMedias={assetMedias} />
          )}
          {element.type === "RECTANGLE" && (
            <RectangleElement element={element} />
          )}
        </div>
      </div>
    </Sequence>
  );
}
